// Fatia S4: Ideias da semana (3 ideias por semana, geradas a partir do perfil, da memória e dos resultados).
import { Info, RefreshCw, Sparkles } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useCan, useCompany } from '../../lib/company'
import { errorMessage } from '../../lib/api'
import { Empty, ErrorState, Loading, PageHead } from '../../ui'
import { creditsText, isQuotaError, useAiUsage } from '../../features/ai/api'
import IdeaCard from '../../features/ideas/IdeaCard'
import { useGenerateIdeas, useIdeas } from '../../features/ideas/api'

const weekLabel = (iso: string) => `Semana de ${iso.split('-').reverse().slice(0, 2).join('/')}`

export default function Ideas() {
  const company = useCompany()
  const canEdit = useCan('company.edit_drafts')
  const q = useIdeas(company.id)
  const gen = useGenerateIdeas(company.id)
  const usage = useAiUsage(company.id, canEdit)

  const data = q.data
  const thisWeek = data?.items.filter((i) => i.week === data.week) ?? []
  const older = data?.items.filter((i) => i.week !== data.week) ?? []
  const groups = [...new Set(older.map((i) => i.week))]

  const generateButton = canEdit && data && (
    <button className="btn primary" onClick={() => gen.mutate()} disabled={gen.isPending || usage.data?.left === 0}>
      {gen.isPending ? <RefreshCw size={16} className="spin" /> : <Sparkles size={16} />}
      {gen.isPending ? 'Pensando em ideias…' : `Gerar agora (${data.manualCredits} créditos)`}
    </button>
  )

  return (
    <>
      <PageHead title="Ideias da semana" sub="Toda semana, 3 ideias de anúncio pensadas para o seu negócio, a partir do seu perfil e dos seus resultados.">
        {generateButton}
      </PageHead>

      {q.isLoading && <Loading lines={6} />}
      {q.error && <ErrorState error={q.error} onRetry={() => q.refetch()} />}

      {data && (
        <div className="stack">
          {!data.weeklyIdeasEnabled && (
            <div className="callout amber">
              <Info size={18} style={{ flexShrink: 0 }} />
              <span>Seu plano não inclui ideias automáticas toda semana. Você ainda pode gerar ideias quando quiser, usando créditos de IA.</span>
            </div>
          )}
          {canEdit && usage.data && <p className="small muted">{creditsText(usage.data.left)}</p>}
          {gen.error && (
            <div className={isQuotaError(gen.error) ? 'callout amber' : 'form-error'} role="alert">{errorMessage(gen.error)}</div>
          )}

          <h2>Esta semana</h2>
          {thisWeek.length === 0 ? (
            <Empty title="Ainda não há ideias para esta semana">
              <p>
                {data.weeklyIdeasEnabled ? 'As ideias novas chegam no domingo à noite. ' : ''}
                {canEdit ? 'Você também pode gerar agora.' : ''}
              </p>
              <p className="small mt-8">
                Se suas contas de anúncio acabaram de ser conectadas, os dados ainda estão sincronizando. As ideias ficam melhores depois disso e com o{' '}
                <Link className="strong" to={`/app/${company.id}/config/perfil-ia`}>perfil para a IA</Link> preenchido.
              </p>
            </Empty>
          ) : (
            <div className="grid g-3">
              {thisWeek.map((i) => <IdeaCard key={i.id} companyId={company.id} idea={i} canEdit={canEdit} />)}
            </div>
          )}

          {groups.map((w) => (
            <section key={w} className="stack" style={{ gap: 12 }}>
              <h2 className="muted" style={{ fontSize: 16 }}>{weekLabel(w)}</h2>
              <div className="grid g-3">
                {older.filter((i) => i.week === w).map((i) => <IdeaCard key={i.id} companyId={company.id} idea={i} canEdit={canEdit} />)}
              </div>
            </section>
          ))}
        </div>
      )}
    </>
  )
}
